import React, { useState, useEffect } from "react";
import { useNavigate, Navigate, useSearchParams } from "react-router-dom";
import { useWeb3 } from "../../context/Web3Context";
import { toast } from "react-toastify";
import "./Home.css";

const LAUNCH_DATE = new Date("2026-04-09T00:00:00").getTime();

function useRunningTimer() {
  const [time, setTime] = useState({ days: 0, hrs: 0, mins: 0, secs: 0, notYet: false });
  useEffect(() => {
    function calc() {
      const now = Date.now();
      const diff = now - LAUNCH_DATE;
      if (diff < 0) { setTime({ days: 0, hrs: 0, mins: 0, secs: 0, notYet: true }); return; }
      setTime({
        days: Math.floor(diff / 86400000),
        hrs: Math.floor((diff % 86400000) / 3600000),
        mins: Math.floor((diff % 3600000) / 60000),
        secs: Math.floor((diff % 60000) / 1000),
        notYet: false,
      });
    }
    calc();
    const id = setInterval(calc, 1000);
    return () => clearInterval(id);
  }, []);
  return time;
}

function pad(n) { return String(n).padStart(2, "0"); }

export default function Home() {
  const { account, connectWallet, connecting } = useWeb3();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const runningTime = useRunningTimer();
  const ref = searchParams.get("ref");

  async function handleConnect() {
    try {
      await connectWallet();
      navigate(ref ? `/home?ref=${ref}` : "/home");
    } catch (err) {
      toast.error(err.message || "Failed to connect wallet");
    }
  }

  if (account) {
    return <Navigate to={ref ? `/home?ref=${ref}` : "/home"} replace />;
  }

  return (
    <div className="welcome-page">
      <div className="welcome-bg-grid" />

      {/* Hero */}
      <section className="welcome-hero">
        <div className="welcome-orb welcome-orb-1" />
        <div className="welcome-orb welcome-orb-2" />
        <div className="welcome-badge">Decentralized Earning Platform</div>
        <h1 className="welcome-title">
          <span className="wt-rain">&#127760;</span>
          <span className="wt-opbnb">GLOBAL</span>
          <span className="wt-rain-text">SHARE</span>
        </h1>
        <p className="welcome-tagline">Earn smart with dual plans, auto upgrades & instant payouts on opBNB Chain</p>
        {ref && <div className="welcome-ref">You've been invited! Sponsor ID: <strong>{ref}</strong></div>}
        <button className="welcome-connect" onClick={handleConnect} disabled={connecting}>
          <span className="wc-glow" /><span className="wc-text">{connecting ? "Connecting..." : "Connect Wallet"}</span>
        </button>
      </section>

      {/* Platform Running Timer */}
      <section className="welcome-section">
        <div className="welcome-timer-card">
          <div className="wtc-header">
            <div className="wtc-header-left">
              <div className="wtc-label">Launched</div>
              <div className="wtc-date">Apr 09, 2026</div>
            </div>
            <div className="wtc-header-right">
              <div className="wtc-pulse" />
              <div className="wtc-running-label">Live</div>
            </div>
          </div>
          <div className="wtc-running-text">Successfully Running For</div>
          <div className="wtc-timer">
            <div className="wtc-unit"><span className="wtc-val">{runningTime.days}</span><span className="wtc-key">D</span></div>
            <span className="wtc-sep">:</span>
            <div className="wtc-unit"><span className="wtc-val">{pad(runningTime.hrs)}</span><span className="wtc-key">H</span></div>
            <span className="wtc-sep">:</span>
            <div className="wtc-unit"><span className="wtc-val">{pad(runningTime.mins)}</span><span className="wtc-key">M</span></div>
            <span className="wtc-sep">:</span>
            <div className="wtc-unit"><span className="wtc-val">{pad(runningTime.secs)}</span><span className="wtc-key">S</span></div>
          </div>
        </div>
      </section>

      {/* Income Types */}
      <section className="welcome-section">
        <h2 className="ws-title">Multiple Income Streams</h2>
        <p className="ws-sub">Earn from 6 different income sources across both plans</p>
        <div className="welcome-income-grid">
          <div className="wi-card"><div className="wi-num">01</div><h3>Direct Referral</h3><p>Earn instantly when your direct referrals activate packages</p></div>
          <div className="wi-card"><div className="wi-num">02</div><h3>Level Income</h3><p>Earn up to 9 levels deep in your matrix network</p></div>
          <div className="wi-card"><div className="wi-num">03</div><h3>Star Share</h3><p>Get your share from the Star plan dividend pool</p></div>
          <div className="wi-card"><div className="wi-num">04</div><h3>Global Share</h3><p>Earn from the Global plan share distribution pool</p></div>
          <div className="wi-card"><div className="wi-num">05</div><h3>Royalty Income</h3><p>Exclusive royalty pool rewards for top performers</p></div>
          <div className="wi-card"><div className="wi-num">06</div><h3>Auto Upgrade</h3><p>Smart system auto-upgrades your packages for maximum earnings</p></div>
        </div>
      </section>

      {/* Features */}
      <section className="welcome-section">
        <h2 className="ws-title">Why Global Share?</h2>
        <p className="ws-sub">Built on opBNB for lightning fast transactions with near zero fees</p>
        <div className="welcome-features">
          <div className="wf-card wf-cyan"><div className="wf-icon">&#9889;</div><h3>Dual Plans</h3><p>Star Plan & Global Plan with unique income structures</p></div>
          <div className="wf-card wf-purple"><div className="wf-icon">&#128176;</div><h3>Auto Upgrades</h3><p>Smart auto-upgrade system maximizes your earnings</p></div>
          <div className="wf-card wf-green"><div className="wf-icon">&#127760;</div><h3>Global Share</h3><p>Earn from global share pool across all packages</p></div>
          <div className="wf-card wf-orange"><div className="wf-icon">&#128184;</div><h3>Royalty Income</h3><p>Exclusive royalty pool for top performers</p></div>
          <div className="wf-card wf-cyan"><div className="wf-icon">&#127765;</div><h3>Referral Income</h3><p>Earn direct referral and level income up to 10 levels</p></div>
          <div className="wf-card wf-purple"><div className="wf-icon">&#128101;</div><h3>9 Level Matrix</h3><p>Build your network with 9 levels of matrix depth</p></div>
        </div>
      </section>

      {/* How Global Share is Different */}
      <section className="welcome-section">
        <h2 className="ws-title">How Global Share is Different</h2>
        <p className="ws-sub">Unlike traditional platforms, Global Share is fully on-chain and unstoppable</p>
        <div className="welcome-diff-grid">
          <div className="wd-item"><span className="wd-check">&#10003;</span><span>No Company</span></div>
          <div className="wd-item"><span className="wd-check">&#10003;</span><span>No Owner</span></div>
          <div className="wd-item"><span className="wd-check">&#10003;</span><span>No Products</span></div>
          <div className="wd-item"><span className="wd-check">&#10003;</span><span>No Office</span></div>
          <div className="wd-item"><span className="wd-check">&#10003;</span><span>No Branch</span></div>
          <div className="wd-item"><span className="wd-check">&#10003;</span><span>No Closing</span></div>
          <div className="wd-item"><span className="wd-check">&#10003;</span><span>No Admin Control</span></div>
          <div className="wd-item"><span className="wd-check">&#10003;</span><span>No Editable</span></div>
          <div className="wd-item"><span className="wd-check">&#10003;</span><span>No Changes</span></div>
          <div className="wd-item"><span className="wd-check">&#10003;</span><span>No PV-BV Matching</span></div>
          <div className="wd-item"><span className="wd-check">&#10003;</span><span>No Waiting</span></div>
          <div className="wd-item"><span className="wd-check">&#10003;</span><span>No Worries</span></div>
        </div>
      </section>

      {/* Trust */}
      <section className="welcome-section">
        <h2 className="ws-title">100% Trust Guaranteed</h2>
        <div className="welcome-trust">
          <div className="wt-item"><span className="wt-check">&#10003;</span>100% Risk Free</div>
          <div className="wt-item"><span className="wt-check">&#10003;</span>100% Decentralized</div>
          <div className="wt-item"><span className="wt-check">&#10003;</span>100% Transparent</div>
          <div className="wt-item"><span className="wt-check">&#10003;</span>100% Secure</div>
          <div className="wt-item"><span className="wt-check">&#10003;</span>100% Distribution</div>
          <div className="wt-item"><span className="wt-check">&#10003;</span>100% Trusted</div>
          <div className="wt-item"><span className="wt-check">&#10003;</span>100% Instant Payout</div>
          <div className="wt-item"><span className="wt-check">&#10003;</span>100% Safe & Secured</div>
        </div>
      </section>

      {/* How It Works */}
      <section className="welcome-section">
        <h2 className="ws-title">How It Works</h2>
        <p className="ws-sub">Get started in 3 simple steps</p>
        <div className="welcome-steps">
          <div className="wstep"><div className="wstep-num">1</div><h3>Connect Wallet</h3><p>Connect your MetaMask, Trust Wallet or any DApp browser wallet</p></div>
          <div className="wstep-line" />
          <div className="wstep"><div className="wstep-num">2</div><h3>Register & Activate</h3><p>Register with a sponsor ID and activate your first package starting from $1</p></div>
          <div className="wstep-line" />
          <div className="wstep"><div className="wstep-num">3</div><h3>Earn & Grow</h3><p>Build your team, earn from multiple streams and watch your income grow</p></div>
        </div>
      </section>

      {/* Wallets + CTA */}
      <section className="welcome-section welcome-section-wallets">
        <h2 className="ws-title">Supported Wallets</h2>
        <div className="welcome-wallets">
          <div className="ww-item"><span className="ww-icon">&#129418;</span><span className="ww-name">MetaMask</span></div>
          <div className="ww-item"><span className="ww-icon">&#128737;</span><span className="ww-name">Trust Wallet</span></div>
          <div className="ww-item"><span className="ww-icon">&#128272;</span><span className="ww-name">SafePal</span></div>
          <div className="ww-item"><span className="ww-icon">&#128178;</span><span className="ww-name">TokenPocket</span></div>
        </div>
        <button className="welcome-connect welcome-connect-bottom" onClick={handleConnect} disabled={connecting}>
          <span className="wc-glow" /><span className="wc-text">{connecting ? "Connecting..." : "Get Started Now"}</span>
        </button>
        <p className="welcome-footer">Smart Contract Powered | opBNB Chain | USDT Based</p>
      </section>
    </div>
  );
}
